// bandExtractor.js
//
// STAGE 1 of the band-layer playback model. PURE DATA — nothing here plays,
// schedules, or renders. It reads the fractal's per-pixel escape values and
// reorganizes them BY BAND: for each distinct escape value (rawIndex) N, where
// does band N appear, column by column, and how thick is it there.
//
// This is the foundation the band-layer playback reads. In the band model, each
// distinct rawIndex value is one concentric "ring" (band) — e.g. on the
// Mandelbrot set, the outermost ring is a low escape value, the next ring the
// next value, and so on. Each band becomes an independent, swept sound layer.
//
// KEY DEFINITION (per the spec):
//   A band is "on" in a column if that column contains ANY of the band's points.
//   Its extent spans the TOPMOST to the BOTTOMMOST band pixel in that column —
//   which JOINS the Mandelbrot's symmetric upper/lower arcs by construction
//   (we take the outermost points regardless of the gap between them), instead
//   of reading them as two separate hits. THICKNESS (the count of band pixels in
//   the column) is captured to drive LOUDNESS later (thicker = louder).
//
// Interior (in-set) pixels belong to no band and are excluded.
//
// DESIGN PRINCIPLE (bands are VIEW-SPECIFIC):
// Bands are defined by the escape values present in a PARTICULAR view, so they
// are not universal — the standard Mandelbrot's bands differ entirely from any
// zoom's or Julia set's. The plan: each shipped fractal PRESET carries its own
// precomputed set of Band Ns (a curated palette the composer picks from for that
// view). A user who navigates to their OWN custom zoom ("their own blowup") has
// no preset band set, so they generate/create their own Band Ns for that view.

/**
 * Extract per-band, per-column presence from a fractal image.
 *
 * @param {Uint32Array|Array} rawIndex  escape value per pixel (row*width+col)
 * @param {Uint8Array|Array}  interior  1 = in-set (excluded), 0 = escaped
 * @param {number} width
 * @param {number} height
 * @param {string} orientation  "vertical" (scan sweeps columns L→R, reading each
 *                              top→bottom) or "horizontal" (sweeps rows T→B).
 *                              Default "vertical".
 * @returns {Map<number, Map<number, {top:number, bottom:number, thickness:number}>>}
 *          band value N → (scan-position → { top, bottom, thickness }).
 *          "top"/"bottom" are the outermost cross-positions of band N at that
 *          scan position (joining symmetric arcs); "thickness" is the pixel count.
 */
export function extractBands(rawIndex, interior, width, height, orientation = "vertical") {
  const isVertical = orientation !== "horizontal";
  // For a vertical scan: scan position = column (0..width-1), cross = row (0..height-1).
  // For a horizontal scan: scan position = row, cross = column.
  const scanCount  = isVertical ? width  : height;
  const crossCount = isVertical ? height : width;
  const idxAt = isVertical
    ? (scan, cross) => cross * width + scan   // column `scan`, row `cross`
    : (scan, cross) => scan * width + cross;  // row `scan`, column `cross`

  // band value N → Map<scanPos, {top, bottom, thickness}>
  const bands = new Map();

  for (let scan = 0; scan < scanCount; scan++) {
    // Within this scan position, accumulate each band's first/last/count.
    // Use a local map so we only touch bands actually present in this column.
    const here = new Map(); // N → {top, bottom, thickness}
    for (let cross = 0; cross < crossCount; cross++) {
      const p = idxAt(scan, cross);
      if (interior[p] === 1) continue;        // in-set: no band
      const N = rawIndex[p];
      let rec = here.get(N);
      if (rec === undefined) {
        here.set(N, { top: cross, bottom: cross, thickness: 1 });
      } else {
        // cross increases monotonically, so bottom is always the latest;
        // top stays the first seen. This spans outermost→outermost (join).
        rec.bottom = cross;
        rec.thickness += 1;
      }
    }
    // Fold this column's findings into the global per-band map.
    for (const [N, rec] of here) {
      let colMap = bands.get(N);
      if (colMap === undefined) { colMap = new Map(); bands.set(N, colMap); }
      colMap.set(scan, rec);
    }
  }

  return bands;
}

/**
 * Summarize an extractBands() result for verification — no audio, just numbers
 * so we can sanity-check the extractor against a known image.
 *
 * @returns {Array<{band, columns, totalPixels, minScan, maxScan,
 *                  avgThickness, maxThickness}>}  sorted by band value ascending.
 */
export function bandSummary(bands) {
  const rows = [];
  for (const [N, colMap] of bands) {
    let total = 0, maxThick = 0, minScan = Infinity, maxScan = -Infinity;
    for (const [scan, rec] of colMap) {
      total += rec.thickness;
      if (rec.thickness > maxThick) maxThick = rec.thickness;
      if (scan < minScan) minScan = scan;
      if (scan > maxScan) maxScan = scan;
    }
    const columns = colMap.size;
    rows.push({
      band: N,
      columns,
      totalPixels: total,
      minScan: minScan === Infinity ? -1 : minScan,
      maxScan: maxScan === -Infinity ? -1 : maxScan,
      avgThickness: columns ? +(total / columns).toFixed(2) : 0,
      maxThickness: maxThick,
    });
  }
  rows.sort((a, b) => a.band - b.band);
  return rows;
}
